import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useI18n } from "@/i18n";
import { callsLeft } from "@/lib/decisionStore";
import * as store from "@/lib/decisionStore";
import { getConsent } from "@/lib/mockAuth";
import { choiceDay } from "@/lib/choiceDate";
import { canResumeIntake } from "@/lib/intakeResume";

const EMPTY_FORM = { title: "", question: "", desiredOutcome: "", horizonDays: 90, chooseBy: "" };

export function useIntake() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { state: navState, search } = useLocation();
  const draftId = navState?.decisionId || new URLSearchParams(search).get("draft");
  const [resumeState, setResumeState] = useState(draftId ? "loading" : "ready");
  const [resumeAttempt, setResumeAttempt] = useState(0);
  const loadedDraftId = useRef(null);
  const aiOn = !!getConsent()?.ai;
  const [step, setStep] = useState(1);
  const [decision, setDecision] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [note, setNote] = useState("");
  const [savedNote, setSavedNote] = useState("");
  const [parsedNote, setParsedNote] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [itemDraft, setItemDraft] = useState({ text: "", epistemic: "" });
  const [optDraft, setOptDraft] = useState({ name: "", description: "", reversibility: "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (!draftId) return;
    if (loadedDraftId.current === draftId && resumeAttempt === 0) return;
    let active = true;
    setResumeState("loading");
    store.getDecision(draftId).then((res) => {
      if (!active) return;
      if (!res.decision) { setResumeState(res.error === "missing" ? "missing" : "error"); return; }
      if (!canResumeIntake(res.decision)) {
        navigate(`/keputusan/${draftId}`, { replace: true });
        return;
      }
      setDecision(res.decision);
      loadedDraftId.current = draftId;
      setForm({
        title: res.decision.title,
        question: res.decision.question,
        desiredOutcome: res.decision.desiredOutcome,
        horizonDays: res.decision.horizonDays,
        chooseBy: choiceDay(res.decision.chooseBy),
      });
      setNote(res.decision.note ?? "");
      setSavedNote(res.decision.note ?? "");
      setResumeState("ready");
    }).catch(() => {
      if (active) setResumeState("error");
    });
    return () => { active = false; };
  }, [draftId, navigate, resumeAttempt]);

  const left = decision ? callsLeft(decision) : 4;  const items = decision?.items ?? [];
  const options = decision?.options ?? [];
  const hasKA = items.some((i) => i.epistemic !== "UNKNOWN");

  const dirty =
    (!decision && !!(form.title || form.question || form.desiredOutcome || form.chooseBy)) ||
    (!!decision && (form.title !== decision.title || form.question !== decision.question ||
      form.desiredOutcome !== decision.desiredOutcome || form.horizonDays !== decision.horizonDays ||
      form.chooseBy !== choiceDay(decision.chooseBy))) ||
    note !== savedNote ||
    !!itemDraft.text ||
    !!optDraft.name ||
    !!optDraft.description;

  const run = useCallback(async (key, errKey, fn) => {
    setBusy(key);
    setErrors({});
    const res = await fn();
    setBusy("");
    if (res.error) {
      setErrors({ form: res.error === "locked" ? t("locked") : t(errKey) });
      return null;
    }
    if (res.decision) setDecision(res.decision);
    return res;
  }, [t]);

  const submitQuestion = async () => {
    if (resumeState !== "ready" || (draftId && !decision)) return;
    const next = {};
    if (form.title.trim().length < 3) next.title = t("err.title");
    if (form.question.trim().length < 12) next.question = t("err.question");
    if (Object.keys(next).length) return setErrors(next);
    const res = await run("draft", "err.saveDraft", () =>
      decision ? store.updateDecision(decision.id, form) : store.createDecision(form),
    );
    if (res) {
      if (!draftId && res.decision) {
        loadedDraftId.current = res.decision.id;
        const query = new URLSearchParams(search);
        query.set("draft", res.decision.id);
        navigate(`/keputusan/baru?${query}`, { replace: true });
      }
      setStep(2);
    }
  };

  const persistNote = async () => {
    if (note === savedNote) return true;
    const res = await run("note", "err.saveNote", () => store.saveNote(decision.id, note));
    if (res) setSavedNote(note);
    return !!res;
  };

  const parse = async () => {
    if (!(await persistNote())) return;
    const res = await run("parse", "err.parse", () => store.intakeAssist(decision.id));
    if (res) {
      setSuggestions(res.suggestions);
      setParsedNote(note);
    }
  };

  const toItems = async () => {
    if (await persistNote()) setStep(3);
  };

  const saveItem = async (text, epistemic, source) => {
    const res = await run("item", "err.saveItem", () => store.addItem(decision.id, { text, epistemic, source }));
    return !!res;
  };

  const dropSuggestion = (id) => setSuggestions((s) => s.filter((x) => x.id !== id));

  const acceptSuggestion = async (s) => {
    if (await saveItem(s.text, "ASSUMED", "suggested")) dropSuggestion(s.id);
  };

  const editSuggestion = async (s, text, epistemic) => {
    if (await saveItem(text, epistemic, "suggested")) dropSuggestion(s.id);
  };

  const addManual = async () => {
    if (itemDraft.text.trim().length < 3) return setErrors({ item: t("err.item") });
    if (await saveItem(itemDraft.text, itemDraft.epistemic, "user")) setItemDraft({ text: "", epistemic: "" });
  };

  const changeStatus = async (itemId, epistemic) => {
    await run("status", "err.saveItem", () => store.setItemStatus(decision.id, itemId, epistemic));
  };

  const addOption = async () => {
    if (optDraft.name.trim().length < 2) return setErrors({ optName: t("err.optName") });
    const res = await run("option", "err.saveOption", () => store.addOption(decision.id, optDraft));
    if (res) setOptDraft({ name: "", description: "", reversibility: "" });
  };

  const finish = (withBrief) =>
    navigate(`/keputusan/${decision.id}`, { state: withBrief ? { compose: true } : null });

  return {
    step, setStep, decision, form, setForm, note, setNote, savedNote, parsedNote, suggestions,
    resumeState, retryResume: () => setResumeAttempt((n) => n + 1),
    itemDraft, setItemDraft, optDraft, setOptDraft, errors, setErrors, busy, aiOn, left, items,
    options, hasKA, dirty, submitQuestion, parse, toItems, acceptSuggestion, editSuggestion,
    dropSuggestion, addManual, changeStatus, addOption, finish,
  };
}
